import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { SIGNAL_META, conceptName } from "@/lib/breakoutLabels";
import { PRIORITY_LABEL, PRIORITY_CLS, ACTION_LABEL } from "@/lib/researchLabels";
import { AlertTriangle, ArrowRight, Search, Layers, UserCheck, CalendarClock,
  Radar, ChevronDown, Database } from "lucide-react";

const CARDS = [
  { key: "product_ideas_tracked", label: "Product ideas tracked", to: "/products", tone: "zinc" },
  { key: "enough_history", label: "Enough history to analyze", to: "/products", tone: "zinc" },
  { key: "watch", label: "Watch", to: "/products?signal=watch", tone: "sky" },
  { key: "interesting", label: "Interesting", to: "/products?signal=interesting", tone: "violet" },
  { key: "strong_amazon_signal", label: "Strong Amazon Signal", to: "/products?signal=strong", tone: "emerald" },
  { key: "investigate", label: "Investigate", to: "/products?signal=investigate", tone: "emerald" },
  { key: "cross_source_candidate", label: "Cross-Source Candidate", to: "/products?signal=cross-source", tone: "rose" },
  { key: "new_listings_appearing", label: "New listings appearing", to: "/products?signal=new", tone: "sky" },
  { key: "grouping_review_pending", label: "Needs grouping review", to: "/grouping", tone: "amber" },
  { key: "nr_review_pending", label: "Needs product-name review", to: "/nr-review", tone: "amber" },
];

const TONE_CLS = {
  zinc: "text-zinc-100", sky: "text-sky-300", violet: "text-violet-300",
  emerald: "text-emerald-300", rose: "text-rose-300", amber: "text-amber-300",
};

function MetricCard({ c, value }) {
  const nav = useNavigate();
  return (
    <button
      data-testid={`metric-${c.key}`}
      onClick={() => nav(c.to)}
      className="text-left border border-[#27272A] bg-[#18181B] p-4 hover:border-[#52525B] transition-colors group"
    >
      <div className={`font-mono text-3xl font-bold ${TONE_CLS[c.tone]}`}>{value ?? "—"}</div>
      <div className="text-[11px] uppercase tracking-widest text-[#A1A1AA] mt-2 flex items-center justify-between">
        <span>{c.label}</span>
        <ArrowRight size={12} className="opacity-0 group-hover:opacity-100 text-[#71717A] transition-opacity" />
      </div>
    </button>
  );
}

function Attention({ items }) {
  const nav = useNavigate();
  if (!items.length)
    return (
      <div data-testid="attention-empty" className="border border-dashed border-[#27272A] p-6 text-center text-xs text-[#71717A] font-mono">
        Nothing needs your attention right now.
      </div>
    );
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <button
          key={i}
          data-testid={`attention-${it.kind}-${i}`}
          onClick={() => nav(it.to)}
          className="w-full text-left border border-[#27272A] bg-[#18181B] p-4 hover:border-[#52525B] transition-colors group flex items-start gap-3"
        >
          <it.icon size={16} className={`${it.color} mt-0.5 shrink-0`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-head font-semibold text-[#F4F4F5] truncate">{it.title}</span>
              <span className={`text-[10px] font-mono px-1.5 py-0.5 border ${it.badgeCls}`}>{it.badge}</span>
            </div>
            <div className="text-xs text-[#A1A1AA] mt-1">{it.why}</div>
            {it.missing && <div className="text-[11px] text-[#71717A] mt-0.5">Missing: {it.missing}</div>}
          </div>
          <span className="text-[11px] font-mono text-emerald-400 flex items-center gap-1 shrink-0 self-center">
            {it.action} <ArrowRight size={12} />
          </span>
        </button>
      ))}
    </div>
  );
}

function DeepValidation() {
  const [open, setOpen] = useState(false);
  const [funnel, setFunnel] = useState(null);
  useEffect(() => {
    if (open && !funnel) api.get("/dashboard", { params: { origin: "seed" } }).then((r) => setFunnel(r.data.funnel));
  }, [open, funnel]);
  return (
    <div className="mt-10 border-t border-[#27272A] pt-6">
      <button data-testid="deep-validation-toggle" onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-xs font-mono text-[#71717A] hover:text-[#A1A1AA]">
        <ChevronDown size={14} className={`transition-transform ${open ? "" : "-rotate-90"}`} />
        Deep Validation (legacy V1 scoring · demo data)
      </button>
      {open && (
        <div className="mt-3 text-xs text-[#71717A] font-mono">
          {!funnel ? "Loading…" : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#27272A] border border-[#27272A]">
              {[["signals_analyzed", "Signals"], ["products_tracked", "Products"], ["accelerating", "Accelerating"],
                ["commercially_validated", "Validated"], ["emerging", "Emerging"]].map(([k, l]) => (
                <div key={k} className="bg-[#18181B] p-3">
                  <div className="text-lg font-bold text-[#F4F4F5]">{funnel[k] ?? 0}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#71717A]">{l}</div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-2 text-[10px]">Scored-product/verdict pipeline. Live radar candidates intentionally do not enter here (anti-circularity).</div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [sum, setSum] = useState(null);
  const [attention, setAttention] = useState([]);
  const [research, setResearch] = useState(null);

  useEffect(() => {
    api.get("/amazon/radar-summary").then((r) => setSum(r.data));
    api.get("/research/top-actions", { params: { limit: 5 } }).then((r) => setResearch(r.data)).catch(() => {});
    api.get("/amazon/trajectory-board").then((r) => {
      const concepts = r.data.concepts || [];
      const items = [];
      concepts
        .filter((c) => ["INVESTIGATE", "CROSS_SOURCE_CANDIDATE"].includes(c.current_signal?.stage))
        .forEach((c) => items.push({
          kind: "investigate", icon: Search, color: "text-emerald-400", to: "/trajectory",
          title: conceptName(c.canonical_concept), badge: SIGNAL_META[c.current_signal.stage]?.label || "Investigate",
          badgeCls: SIGNAL_META[c.current_signal.stage]?.cls,
          why: "Amazon marketplace activity is strong enough for independent confirmation.",
          missing: (c.current_signal.missing || [])[0], action: "Check Google Search",
        }));
      concepts
        .filter((c) => c.current_signal?.stage === "STRONG_AMAZON_SIGNAL")
        .forEach((c) => items.push({
          kind: "grouping", icon: Layers, color: "text-amber-400", to: "/grouping",
          title: conceptName(c.canonical_concept), badge: "Review grouping", badgeCls: SIGNAL_META.STRONG_AMAZON_SIGNAL.cls,
          why: "Several listings are showing activity, but the concept grouping may be too broad.",
          missing: "Grouping/subtype confirmation", action: "Review grouping",
        }));
      setAttention(items);
    });
  }, []);

  const nav = useNavigate();
  if (!sum) return <div className="p-8 text-[#71717A] font-mono text-sm">Loading radar…</div>;

  const nrItem = sum.nr_review_pending > 0 ? [{
    kind: "nrreview", icon: UserCheck, color: "text-amber-400", to: "/nr-review",
    title: `${sum.nr_review_pending} product${sum.nr_review_pending > 1 ? "s" : ""} need identifying`,
    badge: "Product review", badgeCls: SIGNAL_META.WATCH.cls,
    why: "Amazon listings that Breakout Radar could not confidently identify.", action: "Review product",
  }] : [];
  const healthItem = sum.snapshot_warning ? [{
    kind: "health", icon: CalendarClock, color: "text-rose-400", to: "/snapshots",
    title: "Snapshot health issue", badge: "Data health", badgeCls: "text-rose-200 bg-rose-500/15 border-rose-400/45",
    why: sum.snapshot_warning.message,
    missing: `${sum.snapshot_warning.missed} missed · ${sum.snapshot_warning.failed} failed · ${sum.snapshot_warning.suspended} suspended`,
    action: "View snapshot health",
  }] : [];
  const attn = [...attention, ...nrItem, ...healthItem];

  return (
    <div className="p-8 max-w-[1400px]" data-testid="dashboard-radar">
      <div className="mb-2 flex items-center gap-2">
        <Radar className="text-emerald-400" size={22} />
        <h1 className="font-head text-3xl font-bold tracking-tight">Breakout Radar</h1>
        <span data-testid="env-indicator"
          className={`ml-1 text-[10px] font-mono px-2 py-0.5 border rounded-sm ${
            sum.collector_authoritative
              ? "text-emerald-300 bg-emerald-400/10 border-emerald-400/30"
              : "text-sky-300 bg-sky-400/10 border-sky-400/30"}`}>
          {sum.collector_authoritative ? "LIVE · Amazon US New Releases" : "PREVIEW · read-only production mirror"}
        </span>
      </div>
      <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-mono">
        <span className="text-[#A1A1AA]">Source: <span className="text-[#F4F4F5]">{sum.source}</span></span>
        <span className="text-[#71717A]">·</span>
        <span className="text-[#A1A1AA]">
          {sum.collector_authoritative ? "Last Amazon update: " : "Last mirrored snapshot: "}
          <span className="text-[#F4F4F5]">{sum.latest_available_snapshot_at || sum.latest_completed_snapshot_at || "—"}</span>
        </span>
      </div>

      {sum.mirror_info && (
        <div data-testid="mirror-info-banner"
          className="w-full mb-6 flex items-center gap-2 border border-sky-500/30 bg-sky-500/5 text-sky-300 px-4 py-2.5 text-xs font-mono">
          <Database size={14} /> {sum.mirror_info.message}
          {sum.mirror_info.mirror_age_days != null && (
            <span className="text-[#71717A]">· {sum.mirror_info.mirror_age_days}d behind</span>
          )}
        </div>
      )}
      {sum.snapshot_warning && (
        <button data-testid="snapshot-warning-banner" onClick={() => nav("/snapshots")}
          className="w-full mb-6 flex items-center gap-2 border border-rose-500/40 bg-rose-500/10 text-rose-300 px-4 py-2.5 text-xs font-mono hover:bg-rose-500/15 transition-colors">
          <AlertTriangle size={14} /> {sum.snapshot_warning.message}
          <span className="ml-auto flex items-center gap-1">View snapshot health <ArrowRight size={12} /></span>
        </button>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-10">
        {CARDS.map((c) => <MetricCard key={c.key} c={c} value={sum[c.key]} />)}
        <MetricCard c={{ key: "snapshot_health", label: "Snapshot health", to: "/snapshots", tone: sum.snapshot_warning ? "rose" : "emerald" }}
          value={sum.snapshot_warning ? "⚠" : "OK"} />
      </div>

      <section data-testid="needs-attention" className="fadein">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={16} className="text-amber-400" />
          <h2 className="font-head font-semibold tracking-tight">Needs your attention</h2>
          <span className="font-mono text-xs text-[#71717A]">[{attn.length}]</span>
        </div>
        <Attention items={attn} />
      </section>

      {research && research.items?.length > 0 && (
        <section data-testid="research-queue" className="fadein mt-10">
          <div className="flex items-center gap-2 mb-3">
            <Radar size={16} className="text-sky-400" />
            <h2 className="font-head font-semibold tracking-tight">Research queue — what to do next</h2>
            <button onClick={() => nav("/research")} className="ml-auto text-xs font-mono text-sky-300 hover:underline flex items-center gap-1" data-testid="research-queue-open">
              Open research queue <ArrowRight size={12} />
            </button>
          </div>
          <div className="space-y-2">
            {research.items.map((it) => (
              <button key={it.concept} onClick={() => nav("/research")} data-testid={`research-top-${it.concept}`}
                className="w-full text-left border border-[#27272A] bg-[#101013] hover:border-[#3f3f46] px-4 py-3 flex flex-wrap items-center gap-3">
                <span className={`text-[10px] font-mono px-2 py-0.5 border ${PRIORITY_CLS[it.priority] || "text-zinc-300 border-zinc-600"}`}>{PRIORITY_LABEL[it.priority]}</span>
                <span className="font-head font-semibold capitalize">{conceptName(it.concept)}</span>
                <span className="text-xs text-[#71717A]">{it.current_signal_label}</span>
                <span className="ml-auto text-xs font-mono text-emerald-300">{ACTION_LABEL[it.next_action] || it.next_action}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <DeepValidation />
    </div>
  );
}
